import { supabase } from "@/lib/supabase";
import type {
  OnchainContractKey,
  OnchainLinkedWallet,
  OnchainTxInsert,
  OnchainReceiptPatch,
} from "@/lib/types";

export interface TxContextSnapshot {
  privyDid: string;
  uiResolvedSigner: string;
  storedWallet: string | null;
  linkedWallets: OnchainLinkedWallet[] | null;
  chainId: number;
  sponsored: boolean;
}

export interface TxContextTag {
  flow?: string | null;
  questId?: string | number | bigint | null;
  taskId?: string | number | bigint | null;
}

export interface TxLogger {
  log(row: OnchainTxInsert): Promise<void>;
  update(attemptId: string, patch: OnchainReceiptPatch): Promise<void>;
}

export function idFromTag(id: TxContextTag["questId"] | TxContextTag["taskId"]): string | null {
  if (id === null || id === undefined) return null;
  if (typeof id === "bigint") return id.toString();
  if (typeof id === "number") return String(id);
  return id;
}

export function contractKeyFor(
  address: string,
  addresses: Record<OnchainContractKey | string, string>,
): OnchainContractKey | null {
  const a = address.toLowerCase();
  for (const [key, addr] of Object.entries(addresses)) {
    if ((addr ?? "").toLowerCase() === a) {
      return key as OnchainContractKey;
    }
  }
  return null;
}

/**
 * Module-level TxLogger that writes directly to Supabase. Kept outside the
 * service-adapter layer so usePolygonClient doesn't need to consume the
 * ServiceProvider context — the PolygonClient is itself an input to that
 * provider, so depending on it would be circular.
 *
 * Insert/update go through RLS: authenticated users may insert/update only
 * their own rows (privy_did = requesting_user_id()). SELECT is admin-only.
 */
export function createSupabaseTxLogger(): TxLogger {
  return {
    async log(row) {
      const { error } = await supabase.from("onchain_txs").insert({
        attempt_id: row.attemptId,
        chain_id: row.chainId,
        privy_did: row.privyDid,
        ui_resolved_signer: row.uiResolvedSigner,
        stored_wallet: row.storedWallet,
        registry_wallet: row.registryWallet,
        linked_wallets: row.linkedWallets,
        contract_key: row.contractKey,
        method: row.method,
        initiator_flow: row.initiatorFlow,
        related_quest_id: row.relatedQuestId,
        related_task_id: row.relatedTaskId,
        sponsored: row.sponsored,
        status: "pending",
      });
      if (error) throw new Error(`logOnchainTx: ${error.message}`);
    },
    async update(attemptId, patch) {
      const updates: Record<string, unknown> = {};
      if (patch.txHash !== undefined) updates.tx_hash = patch.txHash;
      if (patch.receiptFrom !== undefined) updates.receipt_from = patch.receiptFrom;
      if (patch.status !== undefined) updates.status = patch.status;
      if (patch.blockNumber !== undefined) updates.block_number = patch.blockNumber;
      if (patch.gasUsed !== undefined) updates.gas_used = patch.gasUsed;
      if (patch.effectiveGasPrice !== undefined)
        updates.effective_gas_price = patch.effectiveGasPrice;
      if (patch.errorReason !== undefined) updates.error_reason = patch.errorReason;
      if (patch.confirmedAt !== undefined) updates.confirmed_at = patch.confirmedAt;

      if (Object.keys(updates).length === 0) return;

      const { error } = await supabase
        .from("onchain_txs")
        .update(updates)
        .eq("attempt_id", attemptId);
      if (error) throw new Error(`updateOnchainTxReceipt: ${error.message}`);
    },
  };
}
